/**
 * The vehicle spec a racer enters, and the one number everything hangs off.
 *
 * Sportsman drag racing classes are defined by elapsed time. Weight, power adder
 * and cubic inches matter, but they matter *through* the ET they produce — so
 * this module's real job is to land on a quarter-mile ET and trap speed, by
 * whichever of three routes the racer can offer:
 *
 *   measured   they know their quarter-mile numbers. Believe them.
 *   converted  they only run the eighth. Converted, and flagged as such.
 *   estimated  neither — derived from race weight and flywheel horsepower.
 *
 * The route is carried on the result because it *is* the confidence indicator
 * §4.2 asks for: a class suggestion built on a measured timeslip is worth more
 * than one built on a horsepower number somebody guessed on a forum.
 *
 * Nothing here reads the clock, touches the network, or persists anything —
 * §3's "no data persistence" is a property of the code, not a promise in a
 * paragraph.
 */

export const VEHICLE_TYPES = [
  'Full-bodied car',
  'Convertible',
  'Truck / SUV',
  'Roadster',
  'Altered',
  'Dragster',
] as const;
export type VehicleType = (typeof VEHICLE_TYPES)[number];

/** Open-bodied machines: no window net to fit, so arm restraints do that job. */
export const OPEN_BODIED: VehicleType[] = ['Convertible', 'Roadster', 'Altered', 'Dragster'];

export const POWER_ADDERS = [
  'Naturally aspirated',
  'Single turbo',
  'Twin turbo',
  'Supercharger — roots / screw',
  'Supercharger — centrifugal',
  'Nitrous',
  'Combination (adder + nitrous)',
] as const;
export type PowerAdder = (typeof POWER_ADDERS)[number];

export const TIRES = ['Street tire (DOT)', 'DOT drag radial', 'Slick — bias ply'] as const;
export type Tire = (typeof TIRES)[number];

/**
 * Radial or slick is a different question from how wide, and a whole category
 * of class is written on that axis rather than on width: X275 admits "a
 * 275/60/15 drag radial *or* a 28x10.6 bias slick", Rowdy Radials "235 or
 * smaller drag radial or 26x8.5 or smaller slick". The two are not
 * interchangeable at a given number, which is why the rulebooks name both and
 * give each its own size — so classes list the constructions they admit
 * (`tires` in classes.ts) instead of this being derived from the width.
 *
 * There is deliberately no non-DOT radial here. The radial classes are written
 * on the DOT stamp — that is what "DOT drag radial" means, and why a race-only
 * tire like an ET Street R carries one — and a slick is bias ply by
 * convention. A non-DOT radial is a distinction no rulebook draws, so it is an
 * answer no class could ever admit: offering it could only rule classes out.
 * Add it back when a catalogue arrives that names it.
 */

export const CHASSIS_LEVELS = [
  'Stock — no roll bar',
  'Roll bar — 6-point',
  'Roll cage — 8-point',
  'Roll cage — 10-point',
  'Certified cage (NHRA / SFI 25.x tag)',
  'Full tube chassis, certified',
] as const;
export type ChassisLevel = (typeof CHASSIS_LEVELS)[number];

export const TRANSMISSIONS = [
  'Automatic',
  'Automatic + transbrake',
  'Manual (clutch)',
  'Clutchless manual',
  'Planetary (Lenco / Liberty)',
] as const;
export type Transmission = (typeof TRANSMISSIONS)[number];

export const TRACK_LENGTHS = ['1/4 mile', '1/8 mile'] as const;
export type TrackLength = (typeof TRACK_LENGTHS)[number];

/**
 * Which catalogues to consider.
 *
 * Sanctioned classes and drag-and-drive classes answer different questions, and
 * a racer is nearly always asking one of them rather than both. Showing all of
 * it at once produced twenty-five cards for an ordinary 9-second car, which is
 * not an answer — so this picks the catalogue, and the form asks for the extra
 * drag-and-drive detail only when it is going to be used.
 */
export const INTERESTS = ['NHRA / IHRA', 'Drag Week / Sick Week', 'Radial classes', 'All'] as const;
export type Interest = (typeof INTERESTS)[number];

/**
 * What someone who has picked nothing is asking about.
 *
 * Every catalogue, because a cold arrival has not told us which one they want
 * and a page that quietly answered only part of the question would be wrong for
 * the part it dropped — a Drag Week entrant reading a Super Comp shortlist has
 * no way to tell that the classes they came for were never considered. Long
 * lists are the honest failure here, and they are one button away from being
 * short. This is also the catalogue served at the bare `/tools/classcalc`; the
 * others have URLs of their own (see `catalogue.ts`), so anyone who does know
 * what they are asking can arrive already narrowed.
 */
export const DEFAULT_INTEREST: Interest = 'All';

/**
 * Tread width, as the classes measure it.
 *
 * The order is load-bearing: the drag-and-drive rulebooks state tire rules as
 * ceilings ("no greater than 11.5 inches"), so a narrower tire always satisfies
 * a wider limit. `tireWidthRank` is what lets a 275 car qualify for a small-tire
 * class without listing every combination.
 *
 * Both events measure at the tread with the car's weight on the tire at 20 psi,
 * and neither lets you shave the edges to make the number.
 */
export const TIRE_WIDTHS = [
  '235 or smaller',
  '275 or smaller',
  '11.5 in or smaller — small tire',
  'Over 11.5 in — big tire',
] as const;
export type TireWidth = (typeof TIRE_WIDTHS)[number];

export function tireWidthRank(width: TireWidth): number {
  return TIRE_WIDTHS.indexOf(width);
}

/**
 * Small-block or big-block, as both rulebooks define it.
 *
 * Displacement alone does not settle this. The rule is 430 ci and below for
 * small-block and 431 and up for big-block — but every Mopar 426-based Hemi,
 * Chrysler early Hemi, big-block Mopar, big-block Chevy, big-block Ford and FE
 * Ford runs big-block *regardless of displacement*. A 400 ci BBC is a big-block.
 * So this is asked rather than derived from cubic inches, which would quietly
 * misclassify exactly the engines the rule was written for.
 */

export const ENGINE_FAMILIES = [
  'Small-block — 430 ci and under',
  'Big-block — 431 ci up, or big-block architecture',
  'Not a V8',
] as const;
export type EngineFamily = (typeof ENGINE_FAMILIES)[number];

/**
 * How the car is built, on the three axes the rulebooks actually write rules on.
 *
 * This was one question — "how it's built", four rungs from stock frame to full
 * tube — and one question could not carry it. The rulebooks do not have a single
 * ladder; they have separate paragraphs headed FRAME, FRONT SUSPENSION and REAR
 * SUSPENSION, and a car can sit on a different rung of each. Drag Week's Street
 * Race wants a stock frame *and* a rear suspension in its original pickup points
 * *and* no struts on a car that never had them; X275 wants a stock frame and
 * explicitly does not care what the rear suspension is ("on any type of rear
 * suspension"). Collapsing that into one answer meant either over-blocking cars
 * or letting them into classes their front end rules them out of.
 *
 * The rulebooks treat these as an exact fit rather than a ceiling: Drag Week says
 * a car in Modified "must have modifications that prevent them from running in
 * Super Street", and All Motor "must have modifications that keep you out of
 * Street Race". You do not get to enter a looser class than you have built — see
 * the "lowest class for which they are eligible" rule the page prints beside the
 * results.
 */
export const FRAMES = ['Stock frame', 'Back-halved', 'Full tube chassis'] as const;
export type Frame = (typeof FRAMES)[number];

/**
 * Front suspension, and the one thing that has to be right about the wording.
 *
 * **An aftermarket part in the factory location is still stock-type.** That is
 * not a simplification this page made — it is what the rulebooks say. Ultra
 * Street: "Factory type front suspension only. Coil over shocks are allowed.
 * Aftermarket replacement control arms are allowed." Drag Week's Street Race
 * lets the front suspension use aftermarket components that "bolt in place on
 * the stock frame", and draws its line somewhere else entirely: you "may not use
 * struts on vehicles not factory equipped with struts".
 *
 * So the split is not new-parts-versus-old-parts, and a form that asked it that
 * way would push a car with a set of tubular A-arms and coil-overs out of a
 * class it is perfectly legal in. The split is whether the geometry is still the
 * factory's: same pickup points, same kind of suspension. Struts under a car
 * that never had struts, a Mustang II crossmember and a straight-axle Gasser
 * conversion are all the other answer — and a tubular K-member is not, as long
 * as it puts the arms where the factory did. Ultra Street allows exactly that,
 * in the same breath as "factory type front suspension only".
 */
export const FRONT_SUSPENSIONS = [
  'Stock-type — factory pickup points',
  'Modified — struts, straight axle or relocated pickup points',
] as const;
export type FrontSuspension = (typeof FRONT_SUSPENSIONS)[number];

/**
 * Rear suspension, on the same principle and with the same care.
 *
 * "Stock-type" here means what Drag Week means by it: aftermarket components are
 * fine as long as they "bolt in place on the stock frame in the original pickup
 * points", and the concept is retained — "OE leaf-spring cars must use leaf
 * springs, OE triangulated four-link cars must use triangulated four-links, OE
 * torque-arm cars must use torque arms". A coil-over conversion on a car the
 * factory built with coil springs is explicitly allowed. What is not is a
 * bolt-in or weld-in parallel four-link, a ladder bar, or a non-stock watts
 * link, wishbone or track locator — and those are named separately below because
 * the classes name them separately.
 */
export const REAR_SUSPENSIONS = [
  'Stock-type — factory pickup points',
  'Ladder bar',
  'Parallel four-link',
  'Other non-stock — IRS to solid axle, relocated leafs',
] as const;
export type RearSuspension = (typeof REAR_SUSPENSIONS)[number];

/** Anything that is a departure from the factory's rear suspension concept. */
export function isRearNonStock(rear: RearSuspension): boolean {
  return rear !== 'Stock-type — factory pickup points';
}

/**
 * What it burns — which is a safety question before it is a class question.
 *
 * NHRA's E.T. fuel rule admits nearly everything: "Racing gasoline, gasoline,
 * alcohol, gasohol, diesel, ethanol, natural gas, and propane permitted.
 * Nitromethane prohibited." So the list is not really about eligibility. It is
 * here because one entry on it moves the equipment ladder underneath the driver:
 *
 *   "9.99 and faster, any vehicle, supercharged or turbocharged with alcohol:
 *    Suit meeting SFI Spec 3.2A/15, gloves and boots meeting SFI Spec 3.3/5
 *    mandatory"
 *
 * — two whole layers past the 3.2A/5 the same car on gasoline gets away with,
 * and a rule a racer switching to methanol over the winter will not go looking
 * for. The roots blower on that car picks up a mandatory SFI 14.1 restraint at
 * the same time, where on gasoline it is a class-by-class matter.
 *
 * Methanol is kept apart from E85 deliberately. They get spoken about together
 * as "the alcohol fuels", they are not the same thing to a rulebook, and this
 * page must not be the reason someone brings a two-layer suit to a methanol car.
 */
export const FUELS = [
  'Pump gas',
  'Race gas',
  'E85 / ethanol',
  'Methanol (alcohol)',
  'Diesel',
] as const;
export type Fuel = (typeof FUELS)[number];

/**
 * Is this the fuel NHRA's rules mean when they say "alcohol"?
 *
 * Methanol only. NHRA lists "alcohol" and "ethanol" as separate permitted fuels
 * in the same sentence, and the 3.2A/15 rule is written against the first — so
 * quietly folding E85 in would put two layers of suit on a driver the rulebook
 * does not ask for them from, which is the same defect as leaving them off the
 * methanol car, only in the direction nobody complains about.
 */
export function isAlcohol(fuel: Fuel): boolean {
  return fuel === 'Methanol (alcohol)';
}

export interface VehicleSpec {
  /** Race weight with the driver in it, lb. */
  weight: number | null;
  /** Flywheel horsepower — an estimate is fine, it only feeds the ET estimate. */
  hp: number | null;
  /** Cubic inches. A helper field: it classes Comp and Stock, nothing else here. */
  cubicInches: number | null;
  /** A known best ET, at whichever track length `trackLength` names. */
  bestEt: number | null;
  /** A known best trap speed, mph. */
  bestMph: number | null;
  trackLength: TrackLength;
  interest: Interest;
  vehicleType: VehicleType;
  powerAdder: PowerAdder;
  tire: Tire;
  /** Tread width — what the drag-and-drive classes are actually split on. */
  tireWidth: TireWidth;
  engineFamily: EngineFamily;
  /** What it burns. Moves the safety ladder, not just the class list. */
  fuel: Fuel;
  /** Stock frame, back-halved, or full tube. */
  frame: Frame;
  /** Factory geometry up front, or not — struts, K-member, straight axle. */
  frontSuspension: FrontSuspension;
  /** Factory pickup points out back, or a ladder bar / four-link / conversion. */
  rearSuspension: RearSuspension;
  /** Body model year, for the classes with a year window ('28–'48, '28–'68). */
  bodyYear: number | null;
  /**
   * Registered, insured, plated, and carrying working lights, horn and mirror.
   *
   * The gate on every drag-and-drive class there is: you drive the car between
   * the tracks on public roads, so a trailered race car cannot enter at all.
   */
  streetLegal: boolean;
  chassis: ChassisLevel;
  transmission: Transmission;
  wheelieBars: boolean;
  parachute: boolean;
  fuelCell: boolean;
  masterCutoff: boolean;
  /** A delay box or throttle stop — the line between the bracket classes. */
  electronics: boolean;
  /** The battery has been moved out of its factory location. */
  batteryRelocated: boolean;
}

export const EMPTY_SPEC: VehicleSpec = {
  weight: null,
  hp: null,
  cubicInches: null,
  bestEt: null,
  bestMph: null,
  trackLength: '1/4 mile',
  interest: DEFAULT_INTEREST,
  vehicleType: 'Full-bodied car',
  powerAdder: 'Naturally aspirated',
  tire: 'DOT drag radial',
  tireWidth: '11.5 in or smaller — small tire',
  engineFamily: 'Small-block — 430 ci and under',
  fuel: 'Pump gas',
  frame: 'Stock frame',
  frontSuspension: 'Stock-type — factory pickup points',
  rearSuspension: 'Stock-type — factory pickup points',
  bodyYear: null,
  streetLegal: false,
  chassis: 'Stock — no roll bar',
  transmission: 'Automatic',
  wheelieBars: false,
  parachute: false,
  fuelCell: false,
  masterCutoff: false,
  electronics: false,
  batteryRelocated: false,
};

/**
 * Eighth-mile ET → its quarter-mile equivalent.
 *
 * NHRA prints both columns for every threshold, and the pairs are not a single
 * ratio — 13.99/8.59 works out at 1.629 while 9.99/6.39 and 11.49/7.35 are both
 * about 1.563. This uses the ratio the published pairs agree on across the two
 * tiers that decide the most (the cage/certification line at 9.99 and the
 * roll-bar line at 11.49), which makes it right where it matters most and
 * approximate at the ends.
 *
 * It is deliberately rounded **down**, to 1.5632, below both of those pairs'
 * exact quotients (1.563380 and 1.563265). Rounding up produces a slower
 * quarter-mile equivalent, and a slower equivalent asks for less equipment: at
 * 1.5634 an eighth-mile 6.39 converts to 9.9901 and falls out of the 9.99 tier
 * that requires the cage, the certification and the licence. So the rounding
 * goes the way that keeps a borderline car on the stricter side of a boundary.
 *
 * Hence `source: 'converted'` and the caveat the UI prints beside it: a racer
 * close to a threshold must read the rulebook's own eighth-mile column rather
 * than trust any arithmetic conversion, this one included.
 */
export const EIGHTH_TO_QUARTER = 1.5632;

export function eighthToQuarter(et: number): number {
  return et * EIGHTH_TO_QUARTER;
}

/**
 * And back the other way, for the tracks and classes that run the eighth.
 *
 * Plenty of the sport never sees a quarter — most Tuesday-night test-and-tune
 * lanes are eighth-mile, and Sick Week's Rowdy Radials is raced over it — so a
 * page that only ever prints a quarter-mile number is asking half its readers to
 * do this division in their head.
 *
 * The same ratio serves both directions. Reversing it does technically round the
 * other way (a shade slow in the eighth rather than a shade quick in the
 * quarter), but the gap is under a thousandth of a second at any pace this tool
 * covers — invisible at the hundredth the UI prints, and nothing downstream
 * reads this number. Classing and the safety ladder both run off the quarter,
 * exactly as they did before. A second constant to move a digit nobody can see
 * would be two ratios to keep in step for no gain.
 */
export function quarterToEighth(et: number): number {
  return et / EIGHTH_TO_QUARTER;
}

/**
 * Weight and power → ET and trap speed, by the long-published cube-root
 * approximations (ET = 5.825·∛(lb/hp), mph = 234·∛(hp/lb)).
 *
 * They assume flywheel horsepower and a car that hooks up. A combination that
 * cannot put its power down runs slower than this says, which is the honest
 * direction for a safety tool to be wrong in only if you read it as a floor —
 * so the UI never shows an estimate without saying it is one.
 */
export function estimateFromPower(weight: number, hp: number): { et: number; mph: number } {
  return {
    et: 5.825 * Math.cbrt(weight / hp),
    mph: 234 * Math.cbrt(hp / weight),
  };
}

export type PerfSource = 'measured' | 'converted' | 'estimated';

export interface Performance {
  /** Quarter-mile ET, seconds. The axis everything else hangs off. */
  et: number;
  /** Trap speed, mph — or null when there is no honest way to know it. */
  mph: number | null;
  /** The weakest of the routes that produced these numbers. */
  source: PerfSource;
  /** True when the ET and the mph came from different routes. */
  mixed: boolean;
  /**
   * The same pace over the eighth. Shown, never used: nothing downstream reads
   * it, so the classes and the checklist are the quarter-mile answer they always
   * were.
   */
  eighthEt: number;
  /**
   * How the eighth-mile number was arrived at, which is not always how the
   * quarter was. A racer who runs the eighth gets `measured` here and
   * `converted` on the quarter — the arrow points the other way for everyone
   * else, and it is worth being able to say which.
   */
  eighthSource: PerfSource;
  /**
   * Which length the trap speed was clocked over, or null when there is none.
   *
   * Trap speed does not convert (see below), so with an ET printed at both
   * lengths an unlabelled mph beside them is genuinely ambiguous. This is what
   * lets the UI say which one it belongs to instead of leaving the reader to
   * assume.
   */
  mphLength: TrackLength | null;
}

/**
 * The spec's numbers → what the rest of ClassCalc runs on, or null when the
 * racer has given nothing to work from.
 *
 * **ET is required; trap speed is not.** Every class window and every safety
 * tier is stated in ET, with speed only ever an additional trigger beside it
 * ("9.99 or quicker, *or* 135 mph"). So someone who knows their ET and nothing
 * else gets a full answer, and the speed-only rules simply do not fire — which
 * is the honest outcome, and much better than the tool refusing to say anything
 * because one field is blank. An estimate fills the speed in when weight and
 * power are there to derive it from; otherwise it stays null and the UI says so.
 *
 * A measured number always wins over an estimate, and the two are resolved
 * independently: a racer who knows their ET but not their trap speed gets their
 * real ET and an estimated speed, with `mixed` saying as much.
 *
 * `source` is the *weakest* of the routes used, never the strongest. A result is
 * only as good as the shakiest number in it.
 */
export function resolvePerformance(spec: VehicleSpec): Performance | null {
  const estimate = spec.weight && spec.hp && spec.weight > 0 && spec.hp > 0
    ? estimateFromPower(spec.weight, spec.hp)
    : null;

  const measuredEt = spec.bestEt && spec.bestEt > 0 ? spec.bestEt : null;
  const measuredMph = spec.bestMph && spec.bestMph > 0 ? spec.bestMph : null;
  const eighth = spec.trackLength === '1/8 mile';

  // Both lengths are resolved together, because which of the two is the derived
  // one depends entirely on which the racer ran. Whichever they actually timed
  // is kept exactly as they typed it — converting a number and converting it
  // back would hand someone their own timeslip with the last digit moved.
  let et: number | null = null;
  let etSource: PerfSource | null = null;
  let eighthEt: number | null = null;
  let eighthSource: PerfSource | null = null;
  if (measuredEt !== null) {
    et = eighth ? eighthToQuarter(measuredEt) : measuredEt;
    etSource = eighth ? 'converted' : 'measured';
    eighthEt = eighth ? measuredEt : quarterToEighth(measuredEt);
    eighthSource = eighth ? 'measured' : 'converted';
  } else if (estimate) {
    et = estimate.et;
    etSource = 'estimated';
    // An estimate is an estimate at either length. The conversion cannot make it
    // worse than it already is, so this stays 'estimated' rather than grading
    // down again to 'converted'.
    eighthEt = quarterToEighth(estimate.et);
    eighthSource = 'estimated';
  }

  let mph: number | null = null;
  let mphSource: PerfSource | null = null;
  let mphLength: TrackLength | null = null;
  if (measuredMph !== null) {
    // Trap speed is trap speed — an eighth-mile car simply traps slower, and
    // there is no honest way to scale it to a quarter-mile number. Take it as
    // given, let the ET carry the conversion, and record the length it was
    // clocked over so the page can label it rather than imply the wrong one.
    mph = measuredMph;
    mphSource = eighth ? 'converted' : 'measured';
    mphLength = spec.trackLength;
  } else if (estimate) {
    mph = estimate.mph;
    mphSource = 'estimated';
    // The cube-root approximation is a quarter-mile one whatever the racer
    // picked in the track-length toggle, which only ever described the numbers
    // they were giving — and here they gave none.
    mphLength = '1/4 mile';
  }

  // No ET and no way to derive one is the only case with nothing to say.
  if (et === null || etSource === null || eighthEt === null || eighthSource === null) return null;

  const rank: Record<PerfSource, number> = { measured: 0, converted: 1, estimated: 2 };
  // Deliberately not weakened by `eighthSource`. The eighth-mile figure is
  // printed and nothing more; grading a real quarter-mile timeslip down to
  // "converted" because a second display number was derived from it would put an
  // amber chip on the best answer this tool can give.
  const source = mphSource !== null && rank[mphSource] > rank[etSource] ? mphSource : etSource;

  return { et, mph, source, mixed: mphSource !== null && mphSource !== etSource, eighthEt, eighthSource, mphLength };
}

/** Reads "3,150" the way a racer types it, and refuses anything that is not a number. */
export function parseNumber(text: string): number | null {
  const value = Number(text.replace(/[,\s]/g, ''));
  return text.trim() && Number.isFinite(value) && value > 0 ? value : null;
}

/** Does this chassis carry a certification tag? */
export function isCertified(chassis: ChassisLevel): boolean {
  return chassis === 'Certified cage (NHRA / SFI 25.x tag)' || chassis === 'Full tube chassis, certified';
}

/** Does this chassis have a full cage, as opposed to a bar or nothing? */
export function hasCage(chassis: ChassisLevel): boolean {
  return chassis !== 'Stock — no roll bar' && chassis !== 'Roll bar — 6-point';
}

/** Anything but a bare stock floor — a bar counts. */
export function hasRollBar(chassis: ChassisLevel): boolean {
  return chassis !== 'Stock — no roll bar';
}

export function isPowerAdded(adder: PowerAdder): boolean {
  return adder !== 'Naturally aspirated';
}

export function isAutomatic(trans: Transmission): boolean {
  return trans === 'Automatic' || trans === 'Automatic + transbrake';
}

export function isManual(trans: Transmission): boolean {
  return trans === 'Manual (clutch)' || trans === 'Clutchless manual';
}
